import { useRef, useEffect, useState } from "react";
import "./UwPolicyCommissions.scss";
import { useSelector } from "react-redux";
import { forwardRef, useImperativeHandle } from 'react';
import { useNavigate } from "react-router-dom";
import {
    DialogActions,
    Dialog,
    DialogTitle,
    DialogContent,
    Table,
    TableContainer,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Paper,
    TablePagination,
} from "@mui/material";
import { motion } from "framer-motion";
import { GridLoader, ScaleLoader } from "react-spinners";
import newRequest from "../../../utils/newRequest";
import ConfirmDialog from "../../../components/confirmDialog/confirmDialog";
import CircularButton, { CustomLoadingButton } from "../../../components/ui/CustomButton";
import numeral from "numeral";
import Select from "react-select";
import { useTranslation } from "react-i18next";
import { ToggleSwitch } from "../../../components/toogle/ToggleSwitch";
import { formatNumber, formatStringToNumberV2 } from "../../../utils/genericFunctions";
import { customSelectStyles_dark, customSelectStyles_light } from "../../../components/ui/CustomSelect";
import { MdAdd, MdClose, MdDelete } from "react-icons/md";
import { showToast } from "../../../toastService";


const UwPolicyCommissions = forwardRef(({ policyDocid, plCurCode, productCode, endorsementCode, documentEnabled, controls }, ref) => {
    const { t } = useTranslation();
    const { currentTheme } = useSelector((state) => state.theme);
    const navigate = useNavigate();
    const [isAddCommissionOpen, setIsAddCommissionOpen] = useState(false);
    const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
    const [confirmDialogAction, setConfirmDialogAction] = useState("None");
    const [isNotifyDialogOpen, setIsNotifyDialogOpen] = useState(false);
    const [notifyDialogTitle, setNotifyDialogTitle] = useState("Confirm");
    const [notifyDialogText, setNotifyDialogText] = useState("Text");
    const [objectToAction, setObjectToAction] = useState({
        object_id: "",
        action_text: "",
        action_invoked: "",
    });
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [isLoading, setIsLoading] = useState(true);
    const [isProcessing, setIsProcessing] = useState(false);
    const [policyCommissions, setPolicyCommissions] = useState([]);

    const [productCommissions, setProductCommissions] = useState([]);
    const [selectedProductCommission, setSelectedProductCommission] = useState(null);
    const [commissionBasis, setCommissionBasis] = useState([]);
    const [selectedCommission, setSelectedCommission] = useState(null);
    const [totalFc, setTotalFc] = useState(0);
    const [totalLc, setTotalLc] = useState(0);
    const [currentPage, setCurrentPage] = useState(0);

    const fetchPolicyCommissions = async () => {
        try {
            setIsProcessing(true);
            const res = await newRequest.get(`/uwPolicyCommission/findAll/${policyDocid}`);
            setPolicyCommissions(res.data);
            // set charge basis
            const options = ["Sum Insured", "Premium", "Fixed Amount", "Other Charge"]?.map((code) => ({
                value: code,
                label: code,
            }));
            setCommissionBasis(options);
        } catch (error) {
            console.error("Error fetching excess types:", error);
        } finally {
            setIsProcessing(false);
        }
    };


    const handlePageChange = (event, newPage) => {
        setCurrentPage(newPage);
    };

    const slicedCommissions = policyCommissions.slice(
        currentPage * rowsPerPage,
        currentPage * rowsPerPage + rowsPerPage
    );

    const handleRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 5));
        setCurrentPage(0);
    };

    const handleConfirm = async () => {
        try {
            if (confirmDialogAction === "deleteNumGen") {

            }
        } catch (err) {
            console.log(err);
        }
        // Close the dialog
        setIsConfirmDialogOpen(false);
    };

    const handleCancel = () => {
        setIsConfirmDialogOpen(false);
    };

    const handleCloseNotifyDialog = () => {
        setIsNotifyDialogOpen(false);
    };


    const handleCommissionChange = (e, id, field) => {
        try {
            const newSmiData = [...policyCommissions];
            const index = newSmiData.findIndex(smi => smi._id === id);
            if (index !== -1) {
                if (e && e.target) {
                    // Regular input
                    newSmiData[index][field] = e.target.value;
                } else if (e && typeof e === "object" && e.value !== undefined) {
                    // react-select returns the whole selected option object
                    newSmiData[index][field] = e.value;
                } else {
                    // ToggleSwitch (or direct value like "Y"/"N")
                    newSmiData[index][field] = e;
                }

                setPolicyCommissions(newSmiData);
            }
        } catch (error) {
            console.log(error);
        }
    };


    const handleCommissionBlur = (id, field) => {
        try {
            const updatedData = [...policyCommissions];
            const index = updatedData.findIndex(smi => smi._id === id);
            if (index !== -1) {
                const value = updatedData[index][field];
                if (field === "commission_rate") {
                    updatedData[index][field] = formatNumber(formatStringToNumberV2(value), 4);
                } else {
                    updatedData[index][field] = value;
                }
                updatedData[index]["changed_xx"] = "Y";
                setPolicyCommissions(updatedData);
            }
        } catch (error) {
            console.log(error);
        }
    };

    useImperativeHandle(ref, () => ({
        saveCommissionsFromParent
    }));

    const saveCommissionsFromParent = async () => {
        try {
            saveCommissions();
        } catch (error) {
            console.log(error)
        }
    }


    const saveCommissions = async () => {
        try {
            for (let index = 0; index < policyCommissions.length; index++) {
                const singleRecord = policyCommissions[index];
                const res = await newRequest.put(
                    `/uwPolicyCommission/upd/${singleRecord?._id}`,
                    {
                        ...singleRecord,
                        commission_rate: formatStringToNumberV2(singleRecord?.commission_rate),
                    }
                );
            }
        } catch (error) {
            console.log(error);
        }
    };

    // calculate total fees
    useEffect(() => {
        const totalFc = policyCommissions?.reduce((sum, item) => { return sum + parseFloat(item?.commission_fc_amount); }, 0);
        const totalLc = policyCommissions?.reduce((sum, item) => { return sum + parseFloat(item?.commission_lc_amount); }, 0);
        setTotalFc(totalFc);
        setTotalLc(totalFc);
    }, [policyCommissions]);

    useEffect(() => {
        try {
            setIsLoading(true)
            fetchPolicyCommissions();
        } catch (error) {
            console.log(error);
        } finally {
            setIsLoading(false);
        }
    }, [policyDocid]);

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, transition: { duration: 1 } }}
            exit={{ opacity: 0, transition: { duration: 1 } }}
            className="uw-policy-commissions">
            {isLoading ? (
                <div className="no-content">
                    <ScaleLoader
            color="var(--primary)"
            height={50}
            radius={8}
            width={4}
          />
                </div>
            ) : (
                <div className="uw-policy-commissions-wrapper">
                    {policyCommissions?.length > 0 &&
                        <div className="table-content">
                            <TableContainer component={Paper} sx={{ border: 'none', boxShadow: 'none' }} className="table-container">
                                <Table sx={{ width: "max-content", border: 'none' }}>
                                    <TableHead className="tablehead">
                                        <TableRow className="tablerow">
                                            <TableCell className="table-cell center" style={{ maxWidth: "50px" }}>
                                                #
                                            </TableCell>
                                            <TableCell className="table-cell left" style={{ width: "auto" }}>
                                                <div style={{ display: "flex", width: "100%", flexDirection: "row", alignItems: "center", gap: "10px" }}>
                                                    <span>{t('charge').toUpperCase()}</span>
                                                    {documentEnabled === "Y" &&
                                                        controls?.UW1029 === "Y" &&
                                                        <CircularButton
                                                            currentTheme={currentTheme}
                                                            onClick={async (e) => setIsAddCommissionOpen(true)}
                                                            icon={<MdAdd color="green" />} width={"30px"} height={"30px"} />}
                                                </div>
                                            </TableCell>
                                            <TableCell className="table-cell left" style={{ maxWidth: "150px" }}>
                                                {t('basis').toUpperCase()}
                                            </TableCell>
                                            <TableCell className="table-cell right" style={{ maxWidth: "120px" }}>
                                                {t('rate').toUpperCase()}
                                            </TableCell>
                                            {controls?.UW1028 === "Y" &&
                                                <TableCell className="table-cell center" style={{ maxWidth: "100px" }}>
                                                    {t('override').toUpperCase()}
                                                </TableCell>}
                                            <TableCell className="table-cell right" style={{ maxWidth: "150px" }}>
                                                {`${t('amount').toUpperCase()} [${plCurCode}]`}
                                            </TableCell>
                                            {documentEnabled === "Y" && controls?.UW1030 === "Y" &&
                                                <TableCell className="table-cell center" style={{ maxWidth: "100px" }}>
                                                    {t('action').toUpperCase()}
                                                </TableCell>}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody className="tablebody">
                                        {slicedCommissions?.map((row, index) => (
                                            <TableRow
                                                key={row._id}
                                                className={`${selectedCommission?._id === row._id ? "tablerow current" : "tablerow"}`}
                                                onClick={() => {
                                                    try {
                                                        setSelectedCommission(row);
                                                    } catch (error) {
                                                        console.log(error);
                                                    }
                                                }}
                                            >
                                                <TableCell className="table-cell center" style={{ maxWidth: "50px" }}>
                                                    <span>{index + 1}</span>
                                                </TableCell>
                                                <TableCell className="table-cell left" style={{ width: "auto" }}>
                                                    <div style={{ display: "flex", flexDirection: "column", width: "100%" }}>
                                                        <span className="sub-title">{row?.commission_name}</span>
                                                        <span className="sub-title soft" style={{ fontSize: "10px" }}>{row?.commission_code}</span>
                                                    </div>
                                                </TableCell>
                                                <TableCell className="table-cell left" style={{ maxWidth: "150px" }}>
                                                    {documentEnabled === "Y" &&
                                                        row?.commission_override === "Y" ?
                                                        <Select
                                                            id={`commission_basis_${row?._id}`}
                                                            name={`commission_basis_${row?._id}`}
                                                            menuPortalTarget={document.body}
                                                            styles={{
                                                                ...(currentTheme === "dark" ? customSelectStyles_dark : customSelectStyles_light),
                                                                menuPortal: base => ({
                                                                    ...base,
                                                                    zIndex: 9999,
                                                                })
                                                            }}
                                                            width="140px"
                                                            height="36px"
                                                            options={commissionBasis}
                                                            isSearchable={true}
                                                            placeholder={t('basis')}
                                                            value={commissionBasis.find(option => option.value === row?.commission_basis) || null}
                                                            onChange={(selectedOption) => handleCommissionChange(selectedOption, row?._id, 'commission_basis')}
                                                        /> :
                                                        <span className="sub-title">{row?.commission_basis}</span>}
                                                </TableCell>
                                                <TableCell className="table-cell right" style={{ maxWidth: "120px" }}>
                                                    {documentEnabled === "Y" &&
                                                        row?.commission_override === "Y" ?
                                                        <div className="input-item-with-label">
                                                            <input
                                                                id="commission_rate"
                                                                name="commission_rate"
                                                                type="text"
                                                                value={row?.commission_rate}
                                                                onChange={(e) => handleCommissionChange(e, row?._id, 'commission_rate')}
                                                                onBlur={() => handleCommissionBlur(row?._id, "commission_rate")}
                                                                style={{ width: "100%", textAlign: "right" }}
                                                                className="form-input-item table"
                                                            />
                                                        </div>
                                                        :
                                                        <span>{numeral(row.commission_rate).format("0,0.00")}</span>}
                                                </TableCell>
                                                {controls?.UW1028 === "Y" &&
                                                    <TableCell className="table-cell center" style={{ maxWidth: "100px" }}>
                                                        <ToggleSwitch
                                                            value={row?.commission_override}
                                                            onChange={(val) => handleCommissionChange(val, row._id, "commission_override")}
                                                            label={""}
                                                            labelPosition="end"
                                                            labelWidth="max-content"
                                                            border="N"
                                                            disabled={documentEnabled !== "Y"}
                                                        />
                                                    </TableCell>}
                                                <TableCell className="table-cell right" style={{ maxWidth: "150px" }}>
                                                    <span>{numeral(row?.commission_lc_amount).format("0,0.00")}</span>
                                                </TableCell>
                                                {documentEnabled === "Y" && controls?.UW1030 === "Y" &&
                                                    <TableCell className="table-cell center" style={{ maxWidth: "100px" }}>
                                                        <CircularButton
                                                            currentTheme={currentTheme}
                                                            onClick={async (e) => {
                                                                try {
                                                                    const res = await newRequest.delete(`/uwPolicyCommission/del/${row?._id}`);
                                                                    if (res.data?.msg === "deleted") {
                                                                        setPolicyCommissions((prevData) => prevData.filter(doc => doc?._id !== row?._id));
                                                                        showToast(t('deleted'), "success");
                                                                    } else {
                                                                        showToast("Error deleting charge!", "error");
                                                                    }
                                                                } catch (error) {
                                                                    console.log(error);
                                                                }
                                                            }}
                                                            color="#E53935" icon={<MdDelete/>} width={"30px"} height={"30px"} />
                                                    </TableCell>}
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                                {policyCommissions?.length > rowsPerPage &&
                                    <TablePagination
                                        rowsPerPageOptions={[10]}
                                        rowsPerPage={rowsPerPage}
                                        onRowsPerPageChange={handleRowsPerPage}
                                        component="div"
                                        count={policyCommissions?.length}
                                        rows={policyCommissions}
                                        page={currentPage}
                                        onPageChange={handlePageChange}
                                    />}
                            </TableContainer>
                        </div>}
                    {/* End of charges table */}
                </div>
            )}

            {/* Dialog to notify user about changes being saved */}
            <Dialog open={isNotifyDialogOpen}
                onClose={handleCloseNotifyDialog}
                PaperProps={{
                    sx: {
                        backgroundColor: currentTheme === "dark" ? "#181A20" : "#fff",
                        color: currentTheme === "dark" ? "#fff" : "#000",
                    },
                }}
                slotProps={{
                    backdrop: {
                        sx: {
                            backgroundColor: "rgba(0, 0, 0, 0.7)",
                        },
                    },
                }}>
                <DialogTitle>{notifyDialogTitle}</DialogTitle>
                <DialogContent>
                    <div dangerouslySetInnerHTML={{ __html: notifyDialogText }} />
                </DialogContent>
                <DialogActions>
                    <CustomLoadingButton onClick={handleCloseNotifyDialog}>{t('close')}</CustomLoadingButton>
                </DialogActions>
            </Dialog>
            {/* Confirm Dialog */}
            <ConfirmDialog
                isOpen={isConfirmDialogOpen}
                onClose={handleCancel}
                onConfirm={handleConfirm}
                dialogTitle={t('confirmation')}
                dialogContent={<p>{objectToAction.action_text}</p>}
            />

            {isAddCommissionOpen && (
                <motion.div
                    className="overlay-dialog"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1, transition: { duration: 1 } }}
                    exit={{ opacity: 0, transition: { duration: 1 } }}
                >
                    <div className="overlay-wrapper">
                        <div className="overlay-navbar">
                            <span className="main-title">  {t('addCommission')} </span>
                            <CircularButton
                                currentTheme={currentTheme}
                                onClick={() => {
                                    try {
                                        setIsAddCommissionOpen(false);
                                    } catch (error) {
                                        console.log(error)
                                    }
                                }} icon={<MdClose />} width={"35px"} height={"35px"} />
                        </div>
                        <div className="overlay-content">
                            <div className="input-item-with-label">
                                <label
                                    htmlFor="selectedCommission"
                                    className="form-dropdown-label"
                                    style={{
                                        width: "100px",
                                    }}
                                >
                                    {t('charge').toUpperCase()}
                                </label>
                                <Select
                                    id="selectedCommission"
                                    name="selectedCommission"
                                    menuPortalTarget={document.body}
                                    styles={{
                                        ...(currentTheme === "dark" ? customSelectStyles_dark : customSelectStyles_light),
                                        menuPortal: base => ({
                                            ...base,
                                            zIndex: 9999,
                                        })
                                    }}
                                    width="300px"
                                    value={selectedCommission}
                                    onChange={(selectedOption) => {
                                        try {
                                            setSelectedCommission(selectedOption);
                                        } catch (error) {
                                            console.log(error);
                                        }
                                    }}
                                    options={productCommissions}
                                    isSearchable={true}
                                    placeholder={t('select').toUpperCase()}
                                />
                            </div>
                        </div>
                        <div className="overlay-actions">
                            <CustomLoadingButton
                                currentTheme={currentTheme}
                                iconPosition="end"
                                endIcon={<MdAdd />}
                            // onClick={handleNewDedCondition}
                            >
                                {t('add')}
                            </CustomLoadingButton>
                        </div>
                    </div>
                </motion.div>
            )}

            {isProcessing && (
                <motion.div
                    className="processing-dialog"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1, transition: { duration: 1 } }}
                    exit={{ opacity: 0, transition: { duration: 1 } }}
                >
                    <div className="processing-wrapper">
                        <GridLoader
                            color="#F44336"
                            style={{
                                height: "50px",
                                width: "50px",
                            }}
                        />
                    </div>
                </motion.div>
            )}
        </motion.div>
    )
});

export default UwPolicyCommissions;